import React, { useCallback, useState, KeyboardEvent, FormEvent } from 'react';
import classNames from 'classnames';
import { navigate } from 'gatsby';
import { SearchWithChipsProps } from './models';
import { withPrefix } from 'gatsby';

const SearchWithChips: React.FC<SearchWithChipsProps> = ({
  chips = [],
  paramName = 'q',
  initialQuery = '',
  placeholder = 'Search here',
  className,
  searchButtonText,
  resultPageLink,
}) => {
  const [query, setQuery] = useState(initialQuery);
  const buttonLabel = searchButtonText ?? 'Search';

  const buildSearchUrl = useCallback(
    (term: string) => {
      const basePath = resultPageLink || '/';

      const normalizedBase = basePath.endsWith('/') ? basePath : `${basePath}/`;

      return `${normalizedBase}?${paramName}=${encodeURIComponent(term)}`;
    },
    [paramName, resultPageLink]
  );

  const handleSearch = useCallback(
    (term: string) => {
      const trimmed = term.trim();
      if (!trimmed) return;
      const url = buildSearchUrl(trimmed);
      navigate(url);
    },
    [buildSearchUrl]
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    handleSearch(query);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch(query);
    }
  };

  const handleChipClick = (chipLabel: string) => {
    setQuery(chipLabel);
    handleSearch(chipLabel);
  };

  return (
    <div className={classNames('search-bar', className)}>
      <form className="search-bar__form" onSubmit={handleSubmit}>
        <div className="search-bar__input-wrapper">
          <img
            className="search-bar__icon"
            src={withPrefix(`/images/search.svg`)}
            alt="search.svg"
          />
          <input
            type="text"
            className="search-bar__input"
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Search"
          />
          <button type="submit" className="search-bar__button">
            {buttonLabel}
          </button>
        </div>
      </form>

      {chips.length > 0 && (
        <div className="search-bar__chips">
          {chips.map((chip) => (
            <button
              key={chip}
              type="button"
              className="search-bar__chip"
              onClick={() => handleChipClick(chip)}
            >
              {chip}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchWithChips;
